/**
 * drift-staging.ts — pure orchestration seam for the Hybrid tab's unified
 * "Apply N staged changes" button (TCM Accept-via-Staging blueprint §5.2).
 *
 * WHY A PURE MODULE: the web test harness is renderToStaticMarkup + a mocked
 * `useQuery`, so `useEffect` never runs and component-internal logic can't be
 * asserted (see `reference_web_component_test_harness`). All the assertable
 * pieces — the staged-count math and the cross-source apply fan-out + error
 * aggregation — live here so vitest can cover them at the function seam. The
 * Hybrid tab is a thin shell that wires queries/mutations to these functions.
 *
 * Two sources stage onto one queue on a Hybrid tenant:
 *   - TCM  → State=AcceptedPending  (count = list `?state=AcceptedPending`
 *            TotalCount)
 *   - DSC  → Status=Approved, ProcessedAt=null  (count = `status=Approved&
 *            unprocessedOnly` length)
 *
 * "Apply" fans out to both backends:
 *   - TCM  → POST …/apply-staged → one new TcmMonitorVersion
 *   - DSC  → POST …/process-batched-approvals → one ADO PR per tenant
 *
 * `applyAllStaged` NEVER throws. A failure on one source is collected as a
 * per-source error so the other source's result is still surfaced and the
 * toast can report exactly what landed and what didn't.
 */

import type { TcmBaselineWriteResult } from '@/types/tcm'

/** Shape of `api.processBatchedApprovals(...)` — the DSC apply result.
 * `pullRequests` is absent (not empty) on the Container's early return when
 * `Guardian:CreatePullRequestsForBaseline=false`, so it must stay optional. */
export interface ProcessBatchedResult {
  message: string
  processed: number
  pullRequests?: Array<{
    tenantId: string
    driftCount: number
    prUrl: string
    prId: string
  }>
}

/**
 * The minimal slice of the API client `applyAllStaged` depends on. Declared
 * structurally (not the concrete `tcmApi`/`api` objects) so the test can pass
 * a tiny stub and the fan-out stays decoupled from the rest of the client.
 */
export interface DriftStagingApi {
  applyStagedTcmDrifts(
    tenantId: string,
    comment?: string,
  ): Promise<TcmBaselineWriteResult>
  processBatchedApprovals(tenantId?: string): Promise<ProcessBatchedResult>
}

/** One source's failure, captured rather than thrown. */
export interface ApplySourceError {
  source: 'TCM' | 'DSC'
  error: unknown
}

export interface ApplyAllStagedResult {
  tcm?: TcmBaselineWriteResult
  dsc?: ProcessBatchedResult
  errors: ApplySourceError[]
}

/**
 * Total staged count across both sources. The Hybrid tab gates the Apply
 * affordance on this being > 0 (blueprint §5.3 / §7.7) and renders it in the
 * staged-banner headline.
 *
 * Defensive against undefined/NaN counts — a missing or in-flight query feeds
 * 0, never NaN, so the button-hidden gate and the headline copy stay sane.
 */
export function countStaged({
  tcmStagedTotal,
  dscQueuedCount,
}: {
  tcmStagedTotal: number | undefined | null
  dscQueuedCount: number | undefined | null
}): number {
  const tcm = Number.isFinite(tcmStagedTotal as number) ? (tcmStagedTotal as number) : 0
  const dsc = Number.isFinite(dscQueuedCount as number) ? (dscQueuedCount as number) : 0
  return Math.max(0, tcm) + Math.max(0, dsc)
}

/**
 * Fan out the manual Apply to whichever sources actually have something
 * staged. Both calls fire in parallel; each is independently caught so one
 * source failing (e.g. a TCM 409 "monitor changed since staged") never loses
 * the other source's success. Returns whatever landed plus a per-source error
 * list — NEVER throws.
 *
 * `hasTcmStaged` / `hasDscQueued` let the caller skip a source with nothing
 * staged so we don't issue a guaranteed-409 empty apply (blueprint §7.7).
 */
export async function applyAllStaged({
  tenantId,
  hasTcmStaged,
  hasDscQueued,
  comment,
  api,
}: {
  tenantId: string
  hasTcmStaged: boolean
  hasDscQueued: boolean
  comment?: string
  api: DriftStagingApi
}): Promise<ApplyAllStagedResult> {
  const result: ApplyAllStagedResult = { errors: [] }

  // Run both sources concurrently — they hit independent backends and there's
  // no ordering dependency between a TCM re-baseline and a DSC PR open.
  const tcmPromise = hasTcmStaged
    ? api
        .applyStagedTcmDrifts(tenantId, comment)
        .then(r => {
          result.tcm = r
        })
        .catch(error => {
          result.errors.push({ source: 'TCM', error })
        })
    : Promise.resolve()

  const dscPromise = hasDscQueued
    ? api
        .processBatchedApprovals(tenantId)
        .then(r => {
          result.dsc = r
        })
        .catch(error => {
          result.errors.push({ source: 'DSC', error })
        })
    : Promise.resolve()

  await Promise.all([tcmPromise, dscPromise])
  return result
}

/* -------------------------------------------------------------------------- */
/* Apply-outcome classification (findings F3 / F4)                            */
/* -------------------------------------------------------------------------- */

/**
 * One source's apply verdict the Hybrid tab's onSuccess branch consumes.
 * `ok` rows feed the success copy; `failed` rows each get a toast.error whose
 * message is `getServerMessage(error, fallback)` (the component owns the toast
 * + console.error so the harness can stay at this pure seam).
 */
export interface ApplyOutcomeFailure {
  source: 'TCM' | 'DSC'
  /** The thrown error to feed getServerMessage; undefined for a lying-success
   *  (DSC processed===0) where there's no error object — use the fallback. */
  error: unknown
  /** Per-source advisor-voice fallback when no server message is present. */
  fallback: string
}

export interface ApplyOutcome {
  /** True only when at least one source was attempted AND none failed. */
  allLanded: boolean
  /** TCM landed cleanly (for the success-copy "Monitor refreshed" part). */
  tcmLanded: boolean
  /** DSC landed cleanly AND actually processed > 0 rows. */
  dscLanded: boolean
  /** Sources to surface as failures — includes the DSC processed===0 lie. */
  failures: ApplyOutcomeFailure[]
}

// Per-source fallbacks. No DSC-only "re-scan and re-stage" advice — that's
// only correct for the TCM stale-monitor 409 (finding F3). DSC failures are
// transient/permission shaped, so the copy is a plain retry.
export const TCM_APPLY_FALLBACK =
  'Your staged TCM changes did not apply. Try again.'
export const DSC_APPLY_FALLBACK =
  'Your staged SPO and OneDrive changes did not apply. Try again.'
// Distinct copy for the DSC lying-success (200 but processed===0) — the apply
// did nothing even though rows were queued, so it's a failure but not a
// transport error (finding F4).
export const DSC_NOT_PROCESSED_MESSAGE =
  'Your staged SPO and OneDrive changes were not processed. Check the server log.'

/**
 * Turn an `applyAllStaged` result into the per-source verdict the toast layer
 * needs. Three failure shapes collapse into `failures`:
 *   1. a source in `result.errors` (the call threw) → use its error + fallback;
 *   2. DSC was attempted (`hasDscQueued`) and resolved with `processed === 0`
 *      while rows were queued → a lying success; surface it as a failure with
 *      the dedicated "were not processed" message (finding F4);
 *   3. (TCM has no analogue — apply-staged is all-or-nothing server-side.)
 *
 * `hasDscQueued` is the at-fire-time gate (the component captures it before the
 * mutation runs) so we can tell "DSC wasn't attempted" from "DSC did nothing".
 */
export function classifyApplyOutcome(
  result: ApplyAllStagedResult,
  hasDscQueued: boolean,
): ApplyOutcome {
  const failures: ApplyOutcomeFailure[] = []

  for (const e of result.errors) {
    failures.push({
      source: e.source,
      error: e.error,
      fallback: e.source === 'TCM' ? TCM_APPLY_FALLBACK : DSC_APPLY_FALLBACK,
    })
  }

  // DSC lying success: a 200 with processed===0 while DSC rows were queued at
  // fire time means the apply no-op'd. Only flag it if DSC didn't already throw
  // (no double-reporting) and DSC actually returned a result.
  const dscThrew = result.errors.some(e => e.source === 'DSC')
  const dscProcessedNothing =
    hasDscQueued && !dscThrew && result.dsc !== undefined && result.dsc.processed === 0
  if (dscProcessedNothing) {
    failures.push({
      source: 'DSC',
      error: undefined,
      fallback: DSC_NOT_PROCESSED_MESSAGE,
    })
  }

  const tcmLanded = result.tcm !== undefined && !result.errors.some(e => e.source === 'TCM')
  const dscLanded =
    result.dsc !== undefined && !dscThrew && !dscProcessedNothing && result.dsc.processed > 0

  // "Landed" requires at least one source to have actually succeeded and zero
  // failures. An all-skip (nothing staged) returns allLanded=false so the
  // caller doesn't toast a hollow success — though the component gates Apply on
  // stagedCount>0 so this is belt-and-suspenders.
  const attempted = tcmLanded || dscLanded || failures.length > 0
  const allLanded = attempted && failures.length === 0

  return { allLanded, tcmLanded, dscLanded, failures }
}

/* -------------------------------------------------------------------------- */
/* Bulk-stage shortfall (finding F2)                                          */
/* -------------------------------------------------------------------------- */

export interface BulkStageSourceResult {
  /** ids the caller requested for this source. */
  requested: string[]
  /** ids the server confirmed staged (status==='approved'). */
  failedIds: string[]
  /** True when the whole source call threw (vs a per-row partial failure). */
  threw: boolean
}

export interface BulkStageVerdict {
  /** True when every requested id across both sources staged. */
  allStaged: boolean
  /** Selection keys (`${source}:${id}`) to KEEP selected for a retry. Empty on
   *  full success. TCM is all-or-nothing server-side, so a TCM failure keeps
   *  ALL the requested TCM ids; DSC keeps only the per-row failed ids. */
  keepSelectedKeys: string[]
  /** Sources that fully or partially failed — drives the error toast subject. */
  failedSources: ('TCM' | 'DSC')[]
  /** For the headline math: total requested and total staged across sources. */
  totalRequested: number
  totalStaged: number
}

/**
 * Reconcile a cross-source bulk-stage against what the server actually staged.
 *
 * DSC reports per-row (a 403/404 row fails while siblings stage), so a DSC
 * shortfall keeps exactly the failed DSC ids selected. TCM bulk-approve is
 * all-or-nothing server-side — we don't get per-row results — so a TCM failure
 * (the call threw) keeps ALL requested TCM ids selected; a TCM success keeps
 * none. The keys are `${source}:${id}` to match the Hybrid tab's selection set.
 */
export function summarizeBulkStage({
  dsc,
  tcm,
}: {
  dsc: BulkStageSourceResult
  tcm: BulkStageSourceResult
}): BulkStageVerdict {
  const failedSources: ('TCM' | 'DSC')[] = []
  const keepSelectedKeys: string[] = []

  // DSC: per-row. A thrown call fails the whole DSC batch (keep all requested);
  // otherwise keep only the rows the server didn't confirm.
  const dscFailedIds = dsc.threw ? dsc.requested : dsc.failedIds
  if (dsc.requested.length > 0 && dscFailedIds.length > 0) {
    failedSources.push('DSC')
    dscFailedIds.forEach(id => keepSelectedKeys.push(`DSC:${id}`))
  }

  // TCM: all-or-nothing. Only a thrown call is a failure (no per-row signal).
  if (tcm.requested.length > 0 && tcm.threw) {
    failedSources.push('TCM')
    tcm.requested.forEach(id => keepSelectedKeys.push(`TCM:${id}`))
  }

  const totalRequested = dsc.requested.length + tcm.requested.length
  // Staged = requested minus whatever we're keeping selected (the failures).
  const dscStaged = dsc.requested.length - dscFailedIds.length
  const tcmStaged = tcm.threw ? 0 : tcm.requested.length
  const totalStaged = dscStaged + tcmStaged

  return {
    allStaged: failedSources.length === 0,
    keepSelectedKeys,
    failedSources,
    totalRequested,
    totalStaged,
  }
}

/* -------------------------------------------------------------------------- */
/* Unsupported-row stage refusal (issue #95, wording updated post-#95-B)      */
/* -------------------------------------------------------------------------- */

/**
 * Customer copy for the server's "unsupported drift type" stage refusal.
 * `TcmBaselineWriter.RejectSnapshotDiffRows` 409s a whole TCM bulk-stage when
 * it contains a SnapshotDiff row whose DriftType isn't one of the three
 * handled kinds (Removed/Added/Modified), with a developer-voiced body:
 *
 *   "Tenant '<id>': 1 snapshot-diff drift(s) have an unsupported drift type
 *    and cannot be accepted: <guid> (<resourceType>, <driftType>).
 *    Snapshot-diff removals are retired, additions and modifications are
 *    acknowledged; no other drift type is supported."
 *
 * Narrower than it used to be: #95-B moved SnapshotDiff Added/Modified to
 * acknowledge and Removed to retire, so this refusal now only fires for a
 * malformed/future DriftType, not the ordinary sweep row. Still reachable
 * (a stale selection can carry one), and the raw body still leaks tenant id,
 * drift GUIDs, resource types, and "snapshot-diff" — translate it before it
 * hits a toast. Named for the current wording ("observe-only" is gone from
 * every non-comment code path — grepped Container + Core — so that
 * alternative isn't matched here).
 *
 * Returns the customer sentence when the message carries this refusal, null
 * for anything else so the caller surfaces the original text verbatim.
 */
export function unsupportedStageCopy(serverMessage: string | null | undefined): string | null {
  const msg = (serverMessage ?? '').trim()
  if (!/unsupported drift type/i.test(msg)) return null
  const count = /(\d+)\s+snapshot-diff\s+drift/i.exec(msg)?.[1]
  const n = count ? Number.parseInt(count, 10) : NaN
  const subject =
    Number.isFinite(n) && n > 0
      ? n === 1
        ? "Guardian couldn't stage one of these full-capture findings."
        : `Guardian couldn't stage ${n} of these full-capture findings.`
      : "Guardian couldn't stage some of these full-capture findings."
  return `${subject} Refresh the queue and try again.`
}

/* -------------------------------------------------------------------------- */
/* Undo no-op detection (finding F6)                                          */
/* -------------------------------------------------------------------------- */

/**
 * True when an unstage reverted fewer rows than were requested — i.e. at least
 * one row had already been applied or rejected elsewhere and the Undo was a
 * no-op for it. The Hybrid tab + TcmDriftTable use this to pick honest toast
 * copy instead of always claiming "pulled that one back".
 */
export function isUnstageNoop(revertedCount: number, requestedCount: number): boolean {
  const reverted = Number.isFinite(revertedCount) ? revertedCount : 0
  return reverted < requestedCount
}

/* -------------------------------------------------------------------------- */
/* Staged-banner body copy                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Body sentence for the staged banner, naming exactly what Apply will do for
 * what is ACTUALLY staged right now — never the generic both-lanes sentence
 * when only one lane has rows. Customer-facing: the lanes are named in product
 * words ("watched settings" / "SharePoint and OneDrive"), never TCM/DSC
 * (Monitoring UX plan D5).
 */
export function stagedBannerBody({
  tcmStagedCount,
  dscStagedCount,
  applyPending,
}: {
  tcmStagedCount: number
  dscStagedCount: number
  applyPending: boolean
}): string {
  if (applyPending) {
    return 'Working through the batch now. This usually takes a moment.'
  }
  const hasTcm = tcmStagedCount > 0
  const hasDsc = dscStagedCount > 0
  if (hasTcm && hasDsc) {
    return (
      'Nothing has changed in the tenant yet. Apply lands everything in one pass: ' +
      'a baseline update for your watched settings, plus a pull request for the SharePoint and OneDrive changes.'
    )
  }
  if (hasDsc) {
    return (
      'Nothing has changed in the tenant yet. Apply opens one pull request for the ' +
      'SharePoint and OneDrive changes.'
    )
  }
  // TCM-only — also the safe default if both counts are somehow 0 while the
  // banner renders (the banner is gated on stagedCount > 0 upstream).
  return 'Nothing has changed in the tenant yet. Apply lands the batch as one baseline update.'
}
